import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect } from 'react';

interface OnboardingContextType {
  currentStep: number;
  setStep: (step: number) => void;
  isOpen: boolean;
  hasCompleted: boolean;
  nextStep: () => void;
  prevStep: () => void;
  skipOnboarding: () => void;
  resetOnboarding: () => void;
  completeStep: (step: number) => void;
  completeOnboarding: () => void;
  isStepComplete: (step: number) => boolean;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const ONBOARDING_STORAGE_KEY = 'onboarding-completed';
const MAX_STEPS = 3;

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(ONBOARDING_STORAGE_KEY) === 'true';
    setHasCompleted(completed);
    if (!completed) {
      setIsOpen(true);
    }
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, MAX_STEPS - 1));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  const skipOnboarding = useCallback(() => {
    setIsOpen(false);
  }, []);

  const completeStep = useCallback((step: number) => {
    if (step === currentStep && step < MAX_STEPS - 1) {
      setCurrentStep(step + 1);
    }
  }, [currentStep]);

  const isStepComplete = useCallback((step: number) => {
    return step < currentStep;
  }, [currentStep]);

  const resetOnboarding = useCallback(() => {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    setHasCompleted(false);
    setCurrentStep(0);
    setIsOpen(true);
  }, []);

  const completeOnboarding = useCallback(() => {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, 'true');
    setHasCompleted(true);
    setIsOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      currentStep,
      setStep: setCurrentStep,
      isOpen,
      hasCompleted,
      nextStep,
      prevStep,
      skipOnboarding,
      resetOnboarding,
      completeStep,
      isStepComplete,
      completeOnboarding,
    }),
    [
      currentStep,
      isOpen,
      hasCompleted,
      nextStep,
      prevStep,
      skipOnboarding,
      resetOnboarding,
      completeStep,
      isStepComplete,
      completeOnboarding,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
